// The digest (feature 009) — a pure VIEW over the family's items, computed live in the browser
// (REQ-DIGEST-001). No new item logic: it composes the existing planner models. The three cadences
// (morning / evening / weekly) are time-of-day framings, chosen by `digestPeriod` — there is no
// cloud-cron or send in this slice (delivery = 009b). Health-safe by construction: it only ever
// shows titles that are already clean upstream (Principle 2), never clinical content.
import { todayModel, weekAgenda, isTodo, type DayGroup } from "./planner";
import { localDateStr, type ViewItem } from "./view";

const TZ = "Pacific/Auckland";
export type DigestPeriod = "morning" | "evening" | "weekly";
export const STALE_DAYS = 3; // overdue by ≥ this many days → "stale" (REQ-DIGEST-002)

/** Local hour (0–23) and weekday (0=Sun) for an instant in `tz` — DST-correct via Intl. */
function localParts(nowISO: string, tz: string): { hour: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", hour12: false, weekday: "short" }).formatToParts(new Date(nowISO));
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0") % 24;
  const wd = parts.find((p) => p.type === "weekday")?.value ?? "Sun";
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(wd);
  return { hour, day };
}

/** Which framing to show, by local clock: Sunday evening → the week ahead; otherwise evening after
 *  5pm, else the morning "here's your day". */
export function digestPeriod(nowISO: string, tz = TZ): DigestPeriod {
  const { hour, day } = localParts(nowISO, tz);
  if (day === 0 && hour >= 17) return "weekly";
  if (hour >= 17) return "evening";
  return "morning";
}

/** Tasks overdue by ≥ `thresholdDays` — the chronic slippers, a first-class digest section and a
 *  signal on the Today Slipped line (REQ-DIGEST-002). Oldest first. A subset of "slipped" (any
 *  overdue): only to-dos (never past events or groceries), like attentionStrip. */
export function staleTasks(items: ViewItem[], nowISO: string, thresholdDays = STALE_DAYS, tz = TZ): ViewItem[] {
  const today = localDateStr(new Date(nowISO), tz);
  const todayMs = Date.parse(`${today}T00:00:00Z`);
  return items
    .filter((i) => i.status === "open" && isTodo(i) && i.type !== "grocery" && i.due_at)
    .map((i) => ({ i, overdueDays: Math.round((todayMs - Date.parse(`${localDateStr(new Date(i.due_at as string), tz)}T00:00:00Z`)) / 86_400_000) }))
    .filter(({ overdueDays }) => overdueDays >= thresholdDays)
    .sort((a, b) => b.overdueDays - a.overdueDays)
    .map(({ i }) => i);
}

export interface DigestModel {
  period: DigestPeriod;
  todayItems: ViewItem[]; // what's on today
  slipped: ViewItem[]; // overdue to-dos (all)
  stale: ViewItem[]; // overdue ≥ STALE_DAYS (chronic)
  upcoming: DayGroup[]; // the next-few-days peek (morning/evening) or the 7-day week (weekly)
  needsALook: number; // ingested drafts awaiting review (008), passed in
  counts: { today: number; slipped: number; stale: number; upcoming: number; needsALook: number };
}

/** Compose the digest for a period. Pure; reuses todayModel / weekAgenda / staleTasks. The caller
 *  passes the inbound "Needs a look" count (008) since that lives outside the item list. */
export function digestModel(
  items: ViewItem[],
  nowISO: string,
  period: DigestPeriod = digestPeriod(nowISO),
  opts: { inbound?: number; staleDays?: number; tz?: string } = {}
): DigestModel {
  const tz = opts.tz ?? TZ;
  const today = todayModel(items, nowISO, tz);
  const stale = staleTasks(items, nowISO, opts.staleDays ?? STALE_DAYS, tz);
  // Weekly frames the whole coming week; morning/evening show the short next-few-days peek.
  const upcoming = period === "weekly" ? weekAgenda(items, nowISO, tz, 7).days.slice(1) : today.nextDays;
  const needsALook = opts.inbound ?? 0;
  const upcomingCount = upcoming.reduce((n, d) => n + d.items.length, 0);
  return {
    period,
    todayItems: today.todayItems,
    slipped: today.slipped,
    stale,
    upcoming,
    needsALook,
    counts: { today: today.todayItems.length, slipped: today.slipped.length, stale: stale.length, upcoming: upcomingCount, needsALook },
  };
}
